// =============================================================================
// Dialogs pour le Planning Google Calendar
// =============================================================================

import {
  Plus, Clock, MapPin, Building2, User, ExternalLink, Trash2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { formatAdresse } from "../../utils/clientFormat";

// ── Helpers ─────────────────────────────────────────────────────────────────

function getEventInstallateur(event) {
  if (!event) return "Interne";
  if (event.installateur) return event.installateur;
  const attendees = event.attendees || [];
  if (attendees.length > 0) {
    const ext = attendees.find(a => !a.self);
    if (ext) return ext.displayName || ext.name || ext.email?.split("@")[0] || "Installateur";
  }
  return "Interne";
}

function getEventVisiteur(event) {
  if (!event) return "";
  return event.visiteur || "";
}

function formatDateRange(start, end) {
  if (!start) return "";
  const hasTime = start.includes("T");
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : startDate;

  if (hasTime) {
    const startTime = startDate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    const endTime = endDate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    if (startDate.toDateString() === endDate.toDateString()) {
      return `${startTime} - ${endTime}`;
    }
    const fmt = (d) => d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
    return `${fmt(startDate)} ${startTime} → ${fmt(endDate)} ${endTime}`;
  }

  if (startDate.toDateString() === endDate.toDateString()) {
    return "Journée entière";
  }
  const fmt = (d) => d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  return `${fmt(startDate)} → ${fmt(endDate)}`;
}

// ── Dialog Création ─────────────────────────────────────────────────────────

export function CreateEventDialog({ open, onOpenChange, form, setForm, onSubmit, typesRdv }) {
  const rdvTypes = typesRdv && typesRdv.length > 0 ? typesRdv : [
    { id: "visite_amo", label: "Visite AMO", color_id: "9" },
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-orange-400" />
            Nouveau RDV
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="text-slate-300">Titre *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex: RDV DUPONT"
              className="bg-slate-800 border-slate-700 text-white mt-1"
              data-testid="create-event-title"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">Date début *</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                data-testid="create-event-start-date"
              />
            </div>
            <div>
              <Label className="text-slate-300">Date fin</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">Heure début</Label>
              <Input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value, all_day: false })}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Heure fin</Label>
              <Input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">Visiteur (qui fait le RDV)</Label>
              <Input
                value={form.visiteur}
                onChange={(e) => setForm({ ...form, visiteur: e.target.value })}
                placeholder="Ex: Jean Dupont"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Installateur</Label>
              <Input
                value={form.installateur}
                onChange={(e) => setForm({ ...form, installateur: e.target.value })}
                placeholder="Interne"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-slate-300">Type de chantier</Label>
            <Select
              value={form.type_chantier}
              onValueChange={(v) => {
                const match = rdvTypes.find(t => t.id === v);
                setForm({ ...form, type_chantier: v, color_id: match?.color_id || "9" });
              }}
            >
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rdvTypes.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-slate-300">Adresse</Label>
            <Input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="10 Rue de Paris, 75001 Paris"
              className="bg-slate-800 border-slate-700 text-white mt-1"
            />
          </div>

          <div>
            <Label className="text-slate-300">Description / Notes</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="TEL: 06 XX XX XX XX"
              className="bg-slate-800 border-slate-700 text-white mt-1"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-600">
            Annuler
          </Button>
          <Button onClick={onSubmit} className="bg-orange-600 hover:bg-orange-700" data-testid="create-event-submit">
            Créer le RDV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Dialog Détail ───────────────────────────────────────────────────────────

export function EventDetailDialog({ event, onClose, getEventColor, onDeleteEvent }) {
  if (!event) return null;

  const inst = getEventInstallateur(event);
  const vis = getEventVisiteur(event);
  const colorInfo = getEventColor(event);

  return (
    <Dialog open={!!event} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${colorInfo.bg} ${colorInfo.border} border-2`}></div>
            {event.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-4">
          <div className="flex items-center gap-2 text-slate-300">
            <Clock className="w-4 h-4 text-slate-500" />
            <span>{formatDateRange(event.start, event.end)}</span>
          </div>

          {(() => {
            const loc = formatAdresse(event.location);
            if (!loc) return null;
            return (
              <div className="flex items-start gap-2 text-slate-300">
                <MapPin className="w-4 h-4 mt-0.5 text-slate-500" />
                <span>{loc}</span>
              </div>
            );
          })()}

          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-slate-500" />
            <span className="text-slate-400 text-sm">Installateur :</span>
            <Badge className={`${colorInfo.bg} ${colorInfo.text} text-xs`}>{inst}</Badge>
          </div>

          {vis && (
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-500" />
              <span className="text-slate-400 text-sm">Visiteur :</span>
              <Badge className="bg-emerald-500/20 text-emerald-300 text-xs">{vis}</Badge>
            </div>
          )}

          {event.description && (
            <div className="bg-slate-800 rounded-lg p-3">
              <pre className="text-slate-300 text-sm whitespace-pre-wrap font-sans">
                {event.description}
              </pre>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {event.html_link && (
            <Button
              variant="outline"
              onClick={() => window.open(event.html_link, "_blank")}
              className="border-slate-600"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Google
            </Button>
          )}
          {!event.is_holiday && (
            <Button
              variant="destructive"
              onClick={() => { onDeleteEvent(event.id); onClose(); }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
